import React, { useMemo, useState } from 'react';
import {
  Button,
  InputNumber,
  message,
  Modal,
  Segmented,
  Skeleton,
  Tooltip,
} from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { formatDateET, formatTimeET } from '../../helpers/helpers';
import { useUpdateSettingsMutation } from '../../services/settings';

/* Boarding amber and daycare teal, the same two hues the dashboard's table
   borders and makeup bar use. Hue carries the service; how solid the tile is
   carries its state, so the board still reads in greyscale. */
const BOARDING_HUE = '#F59E0B';
const DAYCARE_HUE = '#14B8A6';

const VIEW_STORAGE_KEY = 'lockerBoardView';

/* "In play" sorts by when the locker frees, so the default view reads left to
   right as the day's turnover. "All" is physical room order, for placing a dog. */
const PLAY_ORDER = { freeing: 0, free: 1, staying: 2, open: 3 };

const tileStyle = locker => {
  const hue = locker.is_boarding ? BOARDING_HUE : DAYCARE_HUE;
  switch (locker.state) {
    case 'staying':
      return {
        background: `${hue}1f`,
        border: `1px solid ${hue}66`,
        color: '#1f2937',
      };
    case 'freeing':
      return {
        background: `${hue}1f`,
        border: `1px solid ${hue}66`,
        borderBottom: `2px dashed ${hue}`,
        color: '#1f2937',
      };
    case 'free':
      return {
        background: '#fff',
        border: `1px solid ${hue}66`,
        color: '#6b7280',
      };
    default:
      return {
        background: '#fff',
        border: '1px solid #eceae7',
        color: '#c3bfba',
      };
  }
};

/* A 54px tile has room for about six characters at this size, so the note is
   the shortest thing that still reads as a clock time: no minutes when they are
   :00, no zone suffix. The whole board is Eastern and the tooltip spells the
   time out in full. "7:00pm ET" overflowed into the neighbouring tile. */
const tileNote = locker => {
  if (locker.state !== 'freeing' || !locker.frees_at) return null;
  const minutes = formatDateET(locker.frees_at, 'mm');
  return formatDateET(locker.frees_at, minutes === '00' ? 'ha' : 'h:mma');
};

const tooltipFor = locker => {
  if (locker.state === 'open') return `Locker ${locker.label} — open`;
  const dogs = (locker.dogs || []).join(', ') || 'No dogs listed';
  const service = locker.is_boarding ? 'Boarding' : 'Daycare';
  const when =
    locker.state === 'free'
      ? locker.freed_at
        ? `Free since ${formatTimeET(locker.freed_at)}`
        : 'Free — checked out'
      : locker.state === 'freeing'
        ? locker.frees_at
          ? `Frees at ${formatTimeET(locker.frees_at)}`
          : 'Frees today'
        : 'Staying past today';
  return (
    <div className="text-xs leading-relaxed">
      <div className="font-semibold">Locker {locker.label}</div>
      <div>{dogs}</div>
      {locker.owner && <div className="opacity-80">{locker.owner}</div>}
      <div className="mt-1">
        {service} · {when}
      </div>
      {locker.conflict && (
        <div className="mt-1">Two appointments claim this locker.</div>
      )}
    </div>
  );
};

const LockerTile = ({ locker }) => (
  <Tooltip title={tooltipFor(locker)}>
    <div
      className="rounded-lg flex flex-col items-center justify-center shrink-0 overflow-hidden px-1"
      style={{ width: 54, height: 54, ...tileStyle(locker) }}
    >
      <span
        className="font-semibold leading-none"
        style={{ fontSize: 15, fontVariantNumeric: 'tabular-nums' }}
      >
        {locker.label}
      </span>
      {locker.state === 'free' ? (
        <CheckOutlined style={{ fontSize: 10, marginTop: 3 }} />
      ) : (
        tileNote(locker) && (
          <span
            className="leading-none mt-1 whitespace-nowrap"
            style={{ fontSize: 9.5, fontVariantNumeric: 'tabular-nums' }}
          >
            {tileNote(locker)}
          </span>
        )
      )}
    </div>
  </Tooltip>
);

const LegendKey = ({ swatch, label }) => (
  <div className="flex items-center gap-1.5">
    <span
      className="rounded shrink-0"
      style={{ width: 12, height: 12, ...swatch }}
    />
    <span className="text-xs text-gray-500">{label}</span>
  </div>
);

const LockerConfigModal = ({ open, onClose, onSaved, from, to }) => {
  const [start, setStart] = useState(from);
  const [end, setEnd] = useState(to);
  const [updateSettings, { isLoading }] = useUpdateSettingsMutation();

  React.useEffect(() => {
    if (open) {
      setStart(from);
      setEnd(to);
    }
  }, [open, from, to]);

  const save = async () => {
    try {
      const res = await updateSettings({
        locker_start: start,
        locker_end: end,
      }).unwrap();
      if (res?.error) throw new Error(res.msg || res.message);
      message.success('Lockers updated');
      // The board is drawn from the daily-ops payload, which lives in a
      // different API slice, so the range change has to be pulled through.
      onSaved?.();
      onClose();
    } catch (err) {
      message.error(
        err?.data?.msg || err.message || 'Could not update the lockers'
      );
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Lockers"
      okText="Save"
      confirmLoading={isLoading}
      onOk={save}
      destroyOnClose
    >
      <p className="text-sm text-gray-500 mb-4">
        The board shows every locker in this range. Change it when the building
        does.
      </p>
      <div className="flex items-end gap-3">
        <div>
          <p className="text-xs text-gray-500 mb-1">First locker</p>
          <InputNumber min={0} value={start} onChange={setStart} />
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Last locker</p>
          <InputNumber min={0} value={end} onChange={setEnd} />
        </div>
        <p className="text-sm text-gray-500 pb-1.5">
          {Number.isInteger(start) && Number.isInteger(end) && end >= start
            ? `${end - start + 1} lockers`
            : '—'}
        </p>
      </div>
    </Modal>
  );
};

const LockerBoard = ({ board, loading, stale, onRangeChange }) => {
  const [view, setView] = useState(
    () => localStorage.getItem(VIEW_STORAGE_KEY) || 'play'
  );
  const [configOpen, setConfigOpen] = useState(false);

  const lockers = useMemo(() => board?.lockers || [], [board]);

  const shown = useMemo(() => {
    if (view === 'all') return lockers;
    return lockers
      .filter(l => l.state !== 'open')
      .slice()
      .sort((a, b) => {
        const byState = PLAY_ORDER[a.state] - PLAY_ORDER[b.state];
        if (byState !== 0) return byState;
        const at = a.frees_at || a.freed_at || '';
        const bt = b.frees_at || b.freed_at || '';
        if (at !== bt) return at.localeCompare(bt);
        return Number(a.label) - Number(b.label);
      });
  }, [lockers, view]);

  const onViewChange = next => {
    setView(next);
    localStorage.setItem(VIEW_STORAGE_KEY, next);
  };

  // An API without the board yet gets no empty card.
  if (!loading && !board) return null;

  const counts = board?.counts || {};
  const total = lockers.length;

  return (
    <div
      className="bg-white rounded-2xl p-4 mb-4"
      style={{
        border: '1px solid #f0ebe6',
        opacity: stale ? 0.6 : 1,
        transition: 'opacity 150ms ease',
      }}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3">
        <p className="text-xs font-medium tracking-wide uppercase text-gray-500 m-0">
          Lockers
        </p>
        {!loading && (
          <p className="text-xs text-gray-500 m-0">
            <span className="font-semibold text-gray-900">
              {counts.freeingToday ?? 0}
            </span>{' '}
            free up today ·{' '}
            <span className="font-semibold text-gray-900">
              {counts.inUse ?? 0}
            </span>{' '}
            in use ·{' '}
            <span className="font-semibold text-gray-900">
              {counts.openNow ?? 0}
            </span>{' '}
            open
          </p>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Segmented
            size="small"
            value={view}
            onChange={onViewChange}
            options={[
              { label: 'In play', value: 'play' },
              { label: `All ${total || ''}`.trim(), value: 'all' },
            ]}
          />
          <Button type="link" size="small" onClick={() => setConfigOpen(true)}>
            Configure
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-wrap gap-1.5">
          {Array.from({ length: 12 }, (_, i) => (
            <Skeleton.Button
              key={i}
              active
              style={{ width: 54, height: 54, minWidth: 54, borderRadius: 8 }}
            />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <p className="text-sm text-gray-400 m-0 py-3">
          No lockers assigned today. Add a locker number in the table below.
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {shown.map(locker => (
            <LockerTile key={locker.label} locker={locker} />
          ))}
        </div>
      )}

      {board?.unknown?.length > 0 && (
        <p className="text-xs text-gray-500 mt-3 mb-0">
          Outside the {board.from}–{board.to} range:{' '}
          {board.unknown.map(l => l.label).join(', ')}. Fix the locker number,
          or widen the range under Configure.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3">
        <LegendKey
          swatch={{
            background: `${BOARDING_HUE}1f`,
            border: `1px solid ${BOARDING_HUE}66`,
          }}
          label="Boarding"
        />
        <LegendKey
          swatch={{
            background: `${DAYCARE_HUE}1f`,
            border: `1px solid ${DAYCARE_HUE}66`,
          }}
          label="Daycare"
        />
        <LegendKey
          swatch={{
            background: '#fff',
            border: `1px solid ${BOARDING_HUE}66`,
            borderBottom: `2px dashed ${BOARDING_HUE}`,
          }}
          label="Frees today"
        />
        <LegendKey
          swatch={{ background: '#fff', border: '1px solid #eceae7' }}
          label="Open"
        />
      </div>

      <LockerConfigModal
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        onSaved={onRangeChange}
        from={board?.from ?? 1}
        to={board?.to ?? 1}
      />
    </div>
  );
};

export default LockerBoard;
